import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api, Plan, UserPlanInfo } from '../services/api';
import './PricingPopup.css';

interface PricingPopupProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlanInfo?: UserPlanInfo;
  onPlanChange?: () => void;
}

export function PricingPopup({ isOpen, onClose, currentPlanInfo, onPlanChange }: PricingPopupProps) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const availablePlans = await api.subscriptions.getPlans();
      setPlans(availablePlans);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError(t('pricingPopup.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planType: 'pro' | 'studio') => {
    try {
      setUpgrading(planType);
      setError(null);
      
      // Call the upgrade endpoint which returns a Stripe checkout URL
      const response = await api.subscriptions.upgradePlan(planType);
      
      // The response should contain a checkout_url to redirect to Stripe
      if (response.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.checkout_url;
      } else {
        throw new Error(t('pricingPopup.errors.noCheckoutUrl'));
      }
      
    } catch (err) {
      console.error('Error upgrading plan:', err);
      setError(t('pricingPopup.errors.upgradeFailed', { planType }));
      setUpgrading(null);
    }
    // Note: Don't set upgrading to null on success since we're redirecting
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 MB';
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) {
      return `${gb.toFixed(gb % 1 === 0 ? 0 : 1)} GB`;
    }
    const mb = bytes / (1024 * 1024);
    return `${Math.round(mb)} MB`;
  };

  const isPlanActive = (planType: string): boolean => {
    return currentPlanInfo?.current_plan.type === planType;
  };

  if (!isOpen) return null;

  return (
    <div className="pricing-popup-overlay" onClick={onClose}>
      <div className="pricing-popup" onClick={(e) => e.stopPropagation()}>
        <div className="pricing-header">
          <div className="pricing-header-content">
            <h2>
              {t('pricingPopup.title').split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </h2>
            {currentPlanInfo && (
              <div className="current-usage">
                <h4>{t('pricingPopup.currentUsage.title')}</h4>
                <div className="usage-stats">
                  <div className="usage-stat">
                    <span className="stat-label">{t('pricingPopup.currentUsage.creditsUsed')}</span>
                    <span className="stat-value">
                      {currentPlanInfo.credits.credits_used} / {currentPlanInfo.current_plan.features.credits_monthly}
                    </span>
                  </div>
                  <div className="usage-stat">
                    <span className="stat-label">{t('pricingPopup.currentUsage.storageUsed')}</span>
                    <span className="stat-value">
                      {formatBytes(currentPlanInfo.storage_used_bytes)} / {formatBytes(currentPlanInfo.storage_limit_bytes)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <button className="close-button" onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 1L1 13M1 1L13 13" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="pricing-loading">{t('pricingPopup.loading')}</div>
        ) : error ? (
          <div className="pricing-error">{error}</div>
        ) : (
          <div className="pricing-content">
            <div className="plans-grid">
              {plans.map((plan) => (
                <div 
                  key={plan.type} 
                  className={`plan-card ${plan.type} ${isPlanActive(plan.type) ? 'active' : ''}`}
                >
                  {plan.type === 'pro' && <div className="popular-badge">{t('pricingPopup.pricing.popular')}</div>}
                  
                  <h3 className="plan-name">{plan.name}</h3>
                  
                  <div className="plan-price">
                    <span className="currency">$</span>
                    <span className="amount">{plan.features.price_usd}</span>
                    <span className="period">{t('pricingPopup.pricing.perUser')}<br />{t('pricingPopup.pricing.perMonth')}</span>
                  </div>

                  <div className="plan-description">
                    {t(`pricingPopup.planDescriptions.${plan.type}`)}
                  </div>

                  <div className="plan-action">
                    {isPlanActive(plan.type) ? (
                      <button className="plan-button current" disabled>
                        {t('pricingPopup.buttons.currentPlan')}
                      </button>
                    ) : plan.type === 'free' ? (
                      <button className="plan-button downgrade" disabled>
                        {t('pricingPopup.buttons.freePlan')}
                      </button>
                    ) : plan.type === 'pro' && currentPlanInfo?.current_plan.type === 'studio' ? (
                      <button 
                        className="plan-button downgrade"
                        onClick={() => handleUpgrade(plan.type as 'pro' | 'studio')}
                        disabled={upgrading !== null}
                      >
                        {upgrading === plan.type ? t('pricingPopup.buttons.processing') : t('pricingPopup.buttons.downgradePlan')}
                      </button>
                    ) : (
                      <button 
                        className="plan-button upgrade"
                        onClick={() => handleUpgrade(plan.type as 'pro' | 'studio')}
                        disabled={upgrading !== null}
                      >
                        {upgrading === plan.type ? t('pricingPopup.buttons.upgrading') : t('pricingPopup.buttons.upgradeNow')}
                      </button>
                    )}
                  </div>

                  <div className="features-header">
                    <h4>{t('pricingPopup.features.title')}</h4>
                    <p>
                      {t(`pricingPopup.features.descriptions.${plan.type}`)}
                    </p>
                  </div>

                  <ul className="plan-features">
                    <li>
                      <img src="/checkmark_icon.svg" alt="✓" className="feature-icon" />
                      <span className="feature-text">
                        <strong>{plan.features.credits_monthly}</strong> {t('pricingPopup.features.creditsPerMonth')}
                      </span>
                    </li>
                    <li>
                      <img src="/checkmark_icon.svg" alt="✓" className="feature-icon" />
                      <span className="feature-text">
                        <strong>{plan.features.gcodes_monthly}</strong> {t('pricingPopup.features.gcodeGenerations')}
                      </span>
                    </li>
                    <li>
                      <img src="/checkmark_icon.svg" alt="✓" className="feature-icon" />
                      <span className="feature-text">
                        <strong>{formatBytes(plan.features.storage_bytes)}</strong> {t('pricingPopup.features.cloudStorage')}
                      </span>
                    </li>
                    <li>
                      <img 
                        src={plan.type === 'free' ? '/xmark_icon.svg' : '/checkmark_icon.svg'} 
                        alt={plan.type === 'free' ? '✗' : '✓'} 
                        className="feature-icon" 
                      />
                      <span className="feature-text">{t('pricingPopup.features.prioritySupport')}</span>
                    </li>
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PricingPopup; 